import axios from "axios"

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://mindalitix-production.up.railway.app/api'

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  /**
   * 30 seconds. This was 500000ms — eight and a half minutes of spinner before a
   * hung request gave up, which on a weak mobile connection is indistinguishable
   * from the app being broken.
   */
  timeout: 30000,
  /**
   * Repeat array params (`?category=A&category=B`) instead of axios's default
   * bracket form (`?category[]=A`).
   *
   * Bracket notation only survives if the server parses queries with `qs`.
   * Express 5 defaults to Node's `querystring`, which reads `category[]` as a
   * literal key — so array filters arrived as undefined and were silently
   * dropped. The repeat form is parsed into an array by both, so the client no
   * longer depends on which parser the API happens to be running.
   */
  paramsSerializer: {
    indexes: null,
  },
})

// Request interceptor to attach JWT token
axiosClient.interceptors.request.use(
  async (config) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Let the browser set the Content-Type (and boundary) for uploads.
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      } else if (config.data === undefined || config.data === null) {
        /**
         * A GET carries no body, so this header described nothing — but it is
         * not on the CORS safelist, and the API is on another origin. Setting it
         * turned every read into a preflight: an OPTIONS round trip before the
         * request the page is actually waiting on, and the API sends no
         * `Access-Control-Max-Age`, so the browser's default caches that answer
         * for five seconds and no longer. On a 300 ms link that is another
         * 300-600 ms in front of the course the sales page cannot render without.
         */
        delete config.headers['Content-Type'];
      } else {
        config.headers['Content-Type'] = 'application/json';
      }
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
    return config;
  },
  (error) => Promise.reject(error),
)

/** Endpoints where a 401 is an expected answer, not an expired session. */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/google-signin', '/auth/forgot-password'];

axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url: string = error.config?.url ?? '';

    if (status === 401 && !AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint))) {
      /**
       * Only a genuinely expired session should sign someone out.
       *
       * This used to hard-assign `window.location.href` on *any* 401, which threw
       * away whatever the learner had on screen — mid-lesson, mid-quiz, mid-form —
       * and did it even for a failed login attempt. A full page load also wipes
       * unsaved state, so a single stale request could cost real work.
       *
       * The token is cleared and the current location is preserved so the user
       * returns to where they were, and the redirect is skipped if we are already
       * on the auth screen.
       */
      /**
       * Read before clearing: a 401 only means "your session ended" if there was
       * a session to end. With no token stored, this is simply an anonymous
       * caller touching a protected endpoint — which happens on public pages,
       * and in the moments after a logout when an in-flight request lands late.
       * Redirecting there threw browsing visitors onto the login screen from the
       * public catalogue, having asked them to sign in to nothing.
       */
      const hadSession = !!localStorage.getItem('token');

      localStorage.removeItem('token');

      const onAuthScreen = window.location.pathname.startsWith('/auth');
      if (hadSession && !onAuthScreen) {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`/auth?returnTo=${returnTo}`);
      }
    } else if (status >= 500) {
      console.error("Server error:", error.response?.data);
    } else if (error.code === "ECONNABORTED") {
      console.error("Request timed out");
    }

    return Promise.reject(error)
  },
)

export default axiosClient
